import { registerService } from './lib';

export type NetworkManagerNetwork = {
  key: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  name: string;
};

export type NetworkManagerToken = {
  key: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  name: string;
  abbreviation: string;
};

export type NetworkManagerTokenNetwork = {
  key: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  network: NetworkManagerNetwork;
  token: NetworkManagerToken;
  contract_address: string | null;
  decimal: number;
  native: boolean;
};

export type NetworkManagerTokenNetworkLiteToken = {
  name: string;
  abbreviation: string;
};

export type NetworkManagerTokenNetworkSummary =
  | NetworkManagerTokenNetwork
  | {
      token: string;
      network: string;
    }
  | {
      token: NetworkManagerTokenNetworkLiteToken;
      network: string;
    };

export function nmPanelTokenNetworkNetworkName(
  tokenNetwork: NetworkManagerTokenNetworkSummary | undefined,
): string | undefined {
  if (!tokenNetwork) return undefined;
  const n = tokenNetwork.network;
  return typeof n === 'string' ? n : n.name;
}

export function nmPanelTokenNetworkTokenName(
  tokenNetwork: NetworkManagerTokenNetworkSummary | undefined,
): string | undefined {
  if (!tokenNetwork) return undefined;
  const tok = tokenNetwork.token;
  return typeof tok === 'string' ? tok : tok.name;
}

export function nmPanelTokenNetworkTokenAbbreviation(
  tokenNetwork: NetworkManagerTokenNetworkSummary | undefined,
): string | undefined {
  if (!tokenNetwork) return undefined;
  const tok = tokenNetwork.token;
  if (typeof tok === 'string') return tok;
  return tok.abbreviation;
}

export function nmPanelTokenNetworkContractAddress(
  tokenNetwork: NetworkManagerTokenNetworkSummary | undefined,
): string | null | undefined {
  if (!tokenNetwork || !('contract_address' in tokenNetwork)) return undefined;
  return tokenNetwork.contract_address;
}

export function nmPanelTokenNetworkDecimal(
  tokenNetwork: NetworkManagerTokenNetworkSummary | undefined,
): number | undefined {
  if (!tokenNetwork || !('decimal' in tokenNetwork)) return undefined;
  return tokenNetwork.decimal;
}

const NM_PANEL_WITHDRAW_TX_EXPLORER = {
  bitcoin: (txHash: string) => `https://mempool.space/tx/${txHash}`,
  bsc: (txHash: string) => `https://bscscan.com/tx/${txHash}`,
  dogecoin: (txHash: string) =>
    `https://blockchair.com/dogecoin/transaction/${txHash}`,
  ethereum: (txHash: string) => `https://etherscan.io/tx/${txHash}`,
  polygon: (txHash: string) => `https://polygonscan.com/tx/${txHash}`,
  ripple: (txHash: string) => `https://livenet.xrpl.org/transactions/${txHash}`,
  solana: (txHash: string) =>
    `https://solscan.io/tx/${encodeURIComponent(txHash)}`,
  tron: (txHash: string) =>
    `https://tronscan.org/#/transaction/${encodeURIComponent(txHash)}`,
} as const;

type NmPanelWithdrawExplorerSlug = keyof typeof NM_PANEL_WITHDRAW_TX_EXPLORER;

function nmPanelWithdrawExplorerSlug(
  networkName: string | undefined,
): NmPanelWithdrawExplorerSlug | undefined {
  if (!networkName?.trim()) return undefined;
  const raw = networkName.trim().toLowerCase();
  if (raw in NM_PANEL_WITHDRAW_TX_EXPLORER) {
    return raw as NmPanelWithdrawExplorerSlug;
  }
  const collapsed = raw.replace(/\s+/g, '');
  if (collapsed in NM_PANEL_WITHDRAW_TX_EXPLORER) {
    return collapsed as NmPanelWithdrawExplorerSlug;
  }
  return undefined;
}

export function nmPanelWithdrawTxExplorerUrl(
  tokenNetwork: NetworkManagerTokenNetworkSummary | undefined,
  txHash: string | undefined,
): string | undefined {
  const hash = txHash?.trim();
  if (!hash) return undefined;
  const network = nmPanelTokenNetworkNetworkName(tokenNetwork);
  const slug = nmPanelWithdrawExplorerSlug(network);
  if (!slug) return undefined;
  return NM_PANEL_WITHDRAW_TX_EXPLORER[slug](hash);
}

export type NetworkManagerWithdrawSummary = {
  key: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  trace_id: string;
  amount: number;
  tx_fee: number;
  tx_hash: string;
  block_number: string;
  status: string;
  tx_index: number;
  token_network: NetworkManagerTokenNetworkSummary;
};

export type NetworkManagerPhrase = {
  key: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  phrase_encrypted: string;
  type: string;
};

export type NetworkManagerAccountSummary = {
  key: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  balance: number;
  locked_balance: number;
};

export type NetworkManagerAccountPanel = NetworkManagerAccountSummary & {
  token: string;
  network: string;
  address: string;
  wallet_type: string;
};

export type NetworkManagerAccountDetails = NetworkManagerAccountSummary & {
  wallet_network: {
    key: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    address: string;
    type: string;
    salt: string;
    account_index: string;
    address_index: string;
    tag: string;
    phrase: NetworkManagerPhrase;
    wallet_network: {
      key: string;
      is_active: boolean;
      created_at: string;
      updated_at: string;
      create_tx: string;
      network: NetworkManagerNetwork;
    }[];
    network_type: NetworkManagerNetwork;
  };
  token_network: NetworkManagerTokenNetwork;
};

export type NetworkManagerWithdrawDetails = NetworkManagerWithdrawSummary & {
  account: string;
  to_address: string;
  from_account: NetworkManagerAccountDetails;
  fee_account: NetworkManagerAccountDetails;
};

export type NetworkManagerBalance = {
  coin: string;
  balance: string;
  balance_usdt?: string;
  balance_iqd?: string;
  percentage_of_total?: number;
};

export type NetworkManagerBalancePanel = {
  balances: NetworkManagerBalance[];
};

export type NetworkManagerCreateWalletParams = {
  coin: string;
  network: string;
};

export type NetworkManagerWalletType = 'hot' | 'warm';

export type NetworkManagerWalletSummery = {
  address: string;
  tag?: string;
};

export type NetworkManagerGetAccountsParams = {
  wallet_type?: NetworkManagerWalletType;
  address?: string;
  network_name?: string;
  min_balance?: string;
  min_locked_balance?: string;
  token_symbol?: string;
  identity_key?: string;
};

declare module './lib' {
  interface ServiceRegistry {
    'network-manager': ServiceEndpoints<{
      'post:create_wallet/': {
        params: NetworkManagerCreateWalletParams;
        response: NetworkManagerWalletSummery;
      };
      'post:refresh/': {
        params: {
          coin: string;
        };
      };
    }>;
    'network-manager-panel': ServiceEndpoints<{
      'get:withdraw_list/': {
        params: {
          search?: string;
          network?: string;
          trace_id?: string;
          token?: string;
          status?: string;
          tx_hash?: string;
          created_at_from?: string;
          created_at_to?: string;
        };
        response: NetworkManagerWithdrawSummary[];
      };
      'get:{slug}/withdraw_detail/': {
        params: {
          slug: string;
        };
        response: NetworkManagerWithdrawDetails;
      };
      'post:{slug}/withdraw_action/': {
        params: {
          slug: string;
          action: 'verify';
        };
        response: NetworkManagerWithdrawSummary;
      };
      'post:cancel-withdraw/': {
        params: {
          key: string;
        };
      };
      'post:accept-withdraw/': {
        params: {
          key: string;
        };
      };
      'post:reject-withdraw/': {
        params: {
          key: string;
        };
      };
      'get:accounts/': {
        params: NetworkManagerGetAccountsParams;
        response: NetworkManagerAccountPanel[];
      };
      'get:account-on-chain-balance/': {
        params: {
          account_id: string;
        };
        response: {
          account_id: string;
          on_chain_balance: string;
        };
      };
      'post:make-aggregation-request/': {
        params: {
          account_key: string;
        };
      };
      'post:make-resource-providing-request/': {
        params: {
          account_key: string;
        };
      };
      'post:check-new-deposit/': {
        params: {
          account_key: string;
        };
      };
      'get:total_on_chain_balances/': {
        params: {
          wallet_type?: NetworkManagerWalletType;
          network?: string;
        };
        response: NetworkManagerBalancePanel;
      };
      'get:total-user-balances/': {
        response: NetworkManagerBalancePanel;
      };
      'get:coin-on-chain-balances/': {
        params: { coin: string; wallet_type?: 'hot' | 'warm' };
        response: {
          coin: string;
          balances: { network: string; balance: string }[];
        };
      };
      'post:refresh/': {
        params: {
          coin: string;
          identity_key: string;
        };
        response: {
          coin: string;
          identity_key: string;
        };
      };
    }>;
  }
}

registerService('network-manager', '/proxy/network-manager/v1/network');
registerService(
  'network-manager-panel',
  '/proxy/network-manager/panel/v1/network',
);
